import Box from "@mui/material/Box";
import { useTranslation } from "next-i18next";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { ProductCard } from "@atoms/ProductCard";
import {
  ProductCardCarousel,
  ProductCardCarouselData,
} from "@atoms/ProductCard/ProductCardCarousel";
import { Section } from "@atoms/Section";
import { WideCard } from "@atoms/WideCard";
import color from "@definitions/mui/color";
import Text from "@ds/components/Text";
import { formattedDate } from "@helpers/dateTime";
import useResponsive from "@helpers/useResponsive";
import { withTranslation } from "next-i18next";
import { useRouter } from "next/router";

const ContentPartial: React.FC<any> = ({ insightList }) => {
  const isMobile = useResponsive("md");
  const router = useRouter();
  const { t } = useTranslation("common");

  const SectionTitle: React.FC<any> = ({ title, spacing, withBorderSeparator, section }) => {
    const queryStr: string = section === "article" ? "Article" : section === "story" ? "Success Story" : "Business Research";

    return (
      <Box bgcolor="white">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt="20px"
          pb={spacing}
          sx={{
            margin: "0 auto",
            width: "calc(100% - 32px)",
            borderTop: withBorderSeparator ? `1px solid ${color.grey.light}` : 0,
          }}
        >
          <Text variant="h2" weight="700">
            {title}
          </Text>
          <Text
            variant="body1"
            weight="700"
            onClick={() => router.push(`/insight/list?category=${queryStr}`)}
            clickable
            sx={{ color: color.primary.main }}
          >
            {`${t("viewAll")}`}
          </Text>
        </Box>
      </Box>
    );
  };

  const renderSections = () => {
    if (!insightList?.data) {
      return;
    }

    const parsedCategoriesDataForCarousel = (category: string) => {
      const temp = [...restData].filter(
        (item) => `${item?.categoryId}`.toLowerCase() === category.toLowerCase(),
      );

      return (
        temp
          .map((item: any) => ({
            id: item.id,
            image: item.imageUrl.mediaPath,
            linkTo: `/insight/${item.slug}`,
            subTitle: formattedDate(item.date),
            title: item.title,
            label: item.category,
          }))
          .splice(0, 4) || []
      );
    };

    const articleCriteria = 'article';

    const firstSectionData = [...insightList?.data].filter(
      (item) => `${item?.categoryId}`.toLowerCase() === articleCriteria
    );

    const restData: ProductCardCarouselData[] = insightList?.data;

    const firstSection = (
      <Section maxWidth="full-width" pb={0} pt={0}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} width="100%">
              {firstSectionData?.[0] && (
                <ProductCard
                  title={firstSectionData[0].title}
                  htmlDescription={firstSectionData[0].story}
                  textColor={color.general.main}
                  subTitle={formattedDate(firstSectionData[0].date)}
                  image={firstSectionData[0].imageUrl.mediaPath}
                  label={firstSectionData[0].category}
                  imageHeight={200}
                  imgQuality={75}
                  height={isMobile ? "auto" : 474}
                  overflow="hidden"
                  linkTo={`/insight/${firstSectionData[0].slug}`}
                  withImagePlaceholder
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {firstSectionData?.slice(1).map((item: any) => (
                <WideCard
                  key={item.id}
                  date={formattedDate(item.date)}
                  description={item.story}
                  title={item.title}
                  image={item.imageUrl.mediaPath}
                  linkTo={`/insight/${item.slug}`}
                  subtitle={item.category}
                  type="border"
                  isGlobalSearch
                />
              ))}
            </Grid>
          </Grid>
        </Container>
      </Section>
    );

    return (
      <>
        <Box sx={{ maxHeight: "calc(100vh - 56px - 29px)", overflow: "auto" }}>
          <Box id="artikel" />
          <SectionTitle title={t("insight.article")} spacing="30px" section="article" />
          {firstSection}
          <Box id="riset">
            <SectionTitle title={t("insight.researchBusiness")} spacing="8px" withBorderSeparator section="research" />
            <Section maxWidth="full-width" pt={2} pb={3}>
              <Container maxWidth="lg" disableGutters>
                <ProductCardCarousel
                  data={parsedCategoriesDataForCarousel('business_research')}
                  labelInline
                  cardHeight={231}
                  imgHeight={84}
                  titleMaxLine={3}
                />
              </Container>
            </Section>
          </Box>
          <Box id="cerita" />
          <SectionTitle title={t("insight.successStory")} spacing="8px" withBorderSeparator section="story" />
          <Section maxWidth="full-width" pt={2} pb={3}>
            <Container maxWidth="lg" disableGutters>
              <ProductCardCarousel
                data={parsedCategoriesDataForCarousel('success_story')}
                labelInline
                cardHeight={231}
                imgHeight={84}
                titleMaxLine={3}
              />
            </Container>
          </Section>
        </Box>
      </>
    );
  };

  return <>{renderSections()}</>;
};

export default withTranslation("common")(ContentPartial);
